/**
 * Client-side video compression using the native MediaRecorder API.
 *
 * Strategy for Karim 2026-02-22:
 *   • Trigger only when file > COMPRESS_THRESHOLD (15 MB) and mime is video/*.
 *   • Downscale to 720p max (keeping aspect ratio) and re-encode at ~1.8 Mbps
 *     video + 96 kbps audio → typical 10-30× reduction on iPhone footage.
 *   • Falls back silently to the original file if the browser doesn't support
 *     MediaRecorder for canvas.captureStream() (very rare in 2026).
 *   • Runs entirely in-browser — no ffmpeg.wasm dependency, no server load.
 *
 * The UI shows a "Compression…" toast while this runs so a heavy iPhone
 * clip doesn't feel like a stall.
 */

export const COMPRESS_THRESHOLD = 15 * 1024 * 1024; // 15 MB

// Target parameters — chosen so a 60 MB iPhone .mov drops to ~5-8 MB with
// crisp-enough quality for a jobsite news feed.
const TARGET_HEIGHT = 720;
const TARGET_VIDEO_BITRATE = 1_800_000;
const TARGET_AUDIO_BITRATE = 96_000;

export function needsCompression(file) {
  return !!(file && (file.type || "").startsWith("video/") && file.size > COMPRESS_THRESHOLD);
}

function pickMimeType() {
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4",
  ];
  for (const m of candidates) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported?.(m)) {
      return m;
    }
  }
  return "";
}

/**
 * Returns a new File (compressed) or the original if compression fails.
 * Never throws — this is a "best-effort" step.
 */
export async function compressVideoIfNeeded(file, onProgress) {
  if (!needsCompression(file)) return file;
  if (typeof document === "undefined" || typeof MediaRecorder === "undefined") return file;

  try {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.src = url;
    await new Promise((resolve, reject) => {
      video.onloadedmetadata = resolve;
      video.onerror = () => reject(new Error("Impossible de charger la vidéo"));
    });

    const duration = video.duration || 0;
    const ratio = video.videoWidth / video.videoHeight || 16 / 9;
    const height = Math.min(TARGET_HEIGHT, video.videoHeight || TARGET_HEIGHT);
    const width = Math.round(height * ratio / 2) * 2; // even value required by many encoders

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D indisponible");

    // Grab a MediaStream from the canvas + the raw audio of the source video.
    const stream = canvas.captureStream(30);
    // captureStream doesn't include audio → we need to route the audio track
    // via HTMLMediaElement.captureStream when supported. Fallback: silent.
    try {
      const audioStream = video.captureStream?.() || video.mozCaptureStream?.();
      if (audioStream) {
        for (const track of audioStream.getAudioTracks()) stream.addTrack(track);
      }
    } catch { /* audio absent → silent output, acceptable */ }

    const mimeType = pickMimeType();
    const recorder = new MediaRecorder(stream, {
      mimeType: mimeType || undefined,
      videoBitsPerSecond: TARGET_VIDEO_BITRATE,
      audioBitsPerSecond: TARGET_AUDIO_BITRATE,
    });
    const chunks = [];
    recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };

    const done = new Promise((resolve) => { recorder.onstop = resolve; });
    recorder.start(500);

    // Drive playback and paint each frame into the canvas.
    await video.play();
    let stopped = false;
    const paint = () => {
      if (stopped) return;
      ctx.drawImage(video, 0, 0, width, height);
      if (duration > 0 && onProgress) {
        try { onProgress(Math.min(1, video.currentTime / duration)); } catch { /* noop */ }
      }
      requestAnimationFrame(paint);
    };
    paint();
    await new Promise((resolve) => { video.onended = resolve; });
    stopped = true;
    recorder.stop();
    await done;

    URL.revokeObjectURL(url);

    const blob = new Blob(chunks, { type: mimeType || "video/webm" });
    if (blob.size >= file.size * 0.9) {
      // Compression didn't help enough → return original to preserve quality.
      return file;
    }
    const ext = (mimeType || "video/webm").includes("mp4") ? "mp4" : "webm";
    const base = (file.name || "video").replace(/\.[^.]+$/, "");
    return new File([blob], `${base}-compressed.${ext}`, { type: blob.type });
  } catch (err) {
    if (typeof console !== "undefined") console.debug("[compressVideo] fallback:", err?.message);
    return file;
  }
}
